/**
 * Older insight generations could store a raw LLM JSON blob (or its truncated
 * prefix) in executive_summary — a legacy backend fallback did this when it
 * failed to parse the model's response (ADO 21572/21573). The backend no
 * longer writes such rows, but existing ones render as JSON until the user
 * regenerates. This pulls the human-readable summary out of a legacy blob,
 * or hides it entirely when nothing readable can be recovered.
 */
export function sanitizeInsightSummary(raw?: string | null): string | null {
  return extractLegacySummary(raw, ['executive_summary', 'summary'])
}

/**
 * Same guard for per-material AI summaries: legacy analyses stored the raw
 * LLM JSON ({"summary": ..., "key_points": ..., "category": ...}) in
 * ai_summary; new analyses store prose. Recover the summary or hide the blob.
 */
export function sanitizeMaterialSummary(raw?: string | null): string | null {
  return extractLegacySummary(raw, ['summary', 'executive_summary'])
}

function extractLegacySummary(raw: string | null | undefined, keys: string[]): string | null {
  if (!raw) return null
  const text = raw.trim()
  // JSON can be fenced, bare, or buried after a prose prefix ("Here are your
  // insights: {...") — detect a blob anywhere, not only at the start.
  const braceIdx = text.search(/[{[]/)
  const looksJsonish = braceIdx !== -1 && /"[\w_]+"\s*:/.test(text.slice(braceIdx))
  if (!text.startsWith('```') && !looksJsonish) return text
  for (const key of keys) {
    const match = text.match(new RegExp(`"${key}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)`))
    if (match && match[1]) {
      try {
        return JSON.parse(`"${match[1]}"`)
      } catch {
        return match[1]
      }
    }
  }
  // No recoverable summary key: keep a substantive prose prefix if one exists,
  // otherwise hide the block rather than render JSON.
  const prefix = braceIdx > 0 ? text.slice(0, braceIdx).replace(/```(?:json)?/g, '').trim() : ''
  return prefix.length >= 20 ? prefix : null
}
